import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BookOpen, Save, Plus, Trash2, Edit2, X, MapPin, Clock,
  Phone, MessageCircle, Instagram, Globe, Mail, ArrowUp, ArrowDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Admin — «База знаний» бутика Angiolini.
 *
 * ИИ-ассистент читает содержимое этой страницы перед каждым ответом
 * клиенту, поэтому здесь должны быть строго факты: адрес, часы, что
 * продаём/чего нет, доставка, оплата, VIP-условия, тон общения.
 *
 * Структура:
 *   • «Контакты» — фиксированный набор полей, обновляется целиком.
 *   • «Разделы» — свободный список карточек (заголовок + текст) с
 *      порядком, добавлением/удалением/перемещением.
 */
export default function AdminKnowledgeBase() {
  const [kb, setKb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingContacts, setSavingContacts] = useState(false);
  const [contactsDraft, setContactsDraft] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [flash, setFlash] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/knowledge-base`, { withCredentials: true });
      setKb(r.data);
      setContactsDraft(r.data.contacts || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const flashOk = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 2000); };

  const saveContacts = async () => {
    setSavingContacts(true);
    try {
      const r = await axios.put(`${API}/admin/knowledge-base/contacts`,
                                  contactsDraft, { withCredentials: true });
      setKb(r.data); setContactsDraft(r.data.contacts || {});
      flashOk("Контакты сохранены");
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось сохранить");
    } finally { setSavingContacts(false); }
  };

  const openNewSection = () => {
    setEditingSection({ title: "", content: "" });
    setShowSectionEditor(true);
  };
  const openEditSection = (s) => {
    setEditingSection({ id: s.id, title: s.title, content: s.content });
    setShowSectionEditor(true);
  };

  const saveSection = async () => {
    if (!editingSection.title.trim() || !editingSection.content.trim()) return;
    try {
      if (editingSection.id) {
        await axios.put(`${API}/admin/knowledge-base/sections/${editingSection.id}`,
                          { title: editingSection.title, content: editingSection.content },
                          { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/knowledge-base/sections`,
                          { title: editingSection.title, content: editingSection.content },
                          { withCredentials: true });
      }
      setShowSectionEditor(false); setEditingSection(null);
      load();
      flashOk("Раздел сохранён");
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось сохранить");
    }
  };

  const removeSection = async (s) => {
    if (!window.confirm(`Удалить раздел «${s.title}»?`)) return;
    await axios.delete(`${API}/admin/knowledge-base/sections/${s.id}`, { withCredentials: true });
    load();
  };

  const moveSection = async (s, delta) => {
    const list = [...(kb?.sections || [])];
    const idx = list.findIndex(x => x.id === s.id);
    const nextIdx = idx + delta;
    if (idx === -1 || nextIdx < 0 || nextIdx >= list.length) return;
    // Swap order values so the AI sees the same sequence users see.
    const a = list[idx], b = list[nextIdx];
    const ao = a.order ?? idx, bo = b.order ?? nextIdx;
    await Promise.all([
      axios.put(`${API}/admin/knowledge-base/sections/${a.id}`, { order: bo }, { withCredentials: true }),
      axios.put(`${API}/admin/knowledge-base/sections/${b.id}`, { order: ao }, { withCredentials: true }),
    ]);
    load();
  };

  if (loading) return <div style={{ color: "#8a8a89", fontFamily: "Manrope" }}>Загрузка…</div>;

  return (
    <div data-testid="admin-knowledge-base" style={{ maxWidth: 960 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <BookOpen size={22} style={{ color: "#C5A059" }} />
        <h2 style={{ margin: 0, fontFamily: "Manrope", fontSize: 20, fontWeight: 700, color: "#0A0A0A" }}>
          База знаний бутика
        </h2>
      </div>
      <div style={{ background: "#FDF6E8", border: "1px solid #F3E2B8", borderRadius: 8,
                    padding: "12px 14px", marginBottom: 20, fontFamily: "Manrope", fontSize: 12,
                    color: "#7A5A15" }}>
        <b>Что это?</b> ИИ-ассистент в WhatsApp читает эту базу перед каждым ответом
        клиенту, поэтому здесь должны быть факты, которые он не должен выдумывать —
        адрес, часы работы, товары, доставка, VIP-условия, брендовый тон. Редактируйте
        свободно: изменения применяются мгновенно.
        {kb?.updated_at && (
          <span style={{ display: "block", marginTop: 6, color: "#8a8a89" }}>
            Последнее обновление: {new Date(kb.updated_at).toLocaleString("ru-RU")}
            {kb.updated_by_name ? ` · ${kb.updated_by_name}` : ""}
          </span>
        )}
      </div>

      {/* CONTACTS */}
      <section style={cardStyle}>
        <div style={sectionHeader}>
          <span style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>Контакты</span>
          <button data-testid="kb-save-contacts" onClick={saveContacts} disabled={savingContacts}
            style={saveBtnStyle}>
            <Save size={13} /> {savingContacts ? "Сохранение…" : "Сохранить контакты"}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <ContactField icon={MapPin} label="Адрес" testid="kb-address"
            value={contactsDraft.address || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, address: v })} />
          <ContactField icon={Clock} label="Часы работы" testid="kb-hours"
            value={contactsDraft.hours || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, hours: v })} />
          <ContactField icon={Phone} label="Телефон" testid="kb-phone"
            value={contactsDraft.phone || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, phone: v })} />
          <ContactField icon={MessageCircle} label="WhatsApp" testid="kb-whatsapp"
            value={contactsDraft.whatsapp || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, whatsapp: v })} />
          <ContactField icon={Instagram} label="Instagram" testid="kb-instagram"
            value={contactsDraft.instagram || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, instagram: v })} />
          <ContactField icon={Globe} label="Сайт" testid="kb-website"
            value={contactsDraft.website || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, website: v })} />
          <ContactField icon={Mail} label="Email" testid="kb-email"
            value={contactsDraft.email || ""}
            onChange={v => setContactsDraft({ ...contactsDraft, email: v })} />
        </div>
      </section>

      {/* SECTIONS */}
      <section style={{ ...cardStyle, marginTop: 18 }}>
        <div style={sectionHeader}>
          <span style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>
            Разделы ({(kb?.sections || []).length})
          </span>
          <button data-testid="kb-new-section" onClick={openNewSection} style={saveBtnStyle}>
            <Plus size={13} /> Новый раздел
          </button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {(kb?.sections || []).length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#8a8a89",
                          fontFamily: "Manrope", fontSize: 13 }}>
              Нет разделов — добавьте первый.
            </div>
          )}
          {(kb?.sections || []).map((s, i, arr) => (
            <div key={s.id} data-testid={`kb-section-${s.id}`}
              style={{ background: "#fafaf9", border: "1px solid #e5e7eb",
                       borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
                <div style={{ flex: 1, fontFamily: "Manrope", fontWeight: 700, fontSize: 14, color: "#0A0A0A" }}>
                  {s.title}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn onClick={() => moveSection(s, -1)} title="Вверх" disabled={i === 0}>
                    <ArrowUp size={13} />
                  </IconBtn>
                  <IconBtn onClick={() => moveSection(s, 1)} title="Вниз" disabled={i === arr.length - 1}>
                    <ArrowDown size={13} />
                  </IconBtn>
                  <IconBtn onClick={() => openEditSection(s)} title="Редактировать"
                    data-testid={`kb-edit-${s.id}`}>
                    <Edit2 size={13} />
                  </IconBtn>
                  <IconBtn onClick={() => removeSection(s)} title="Удалить"
                    data-testid={`kb-delete-${s.id}`}>
                    <Trash2 size={13} />
                  </IconBtn>
                </div>
              </div>
              <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#3f3f46",
                             whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                {s.content}
              </div>
            </div>
          ))}
        </div>
      </section>

      {flash && (
        <div style={{ position: "fixed", bottom: 20, right: 20,
                       background: "#065f46", color: "white",
                       padding: "10px 16px", borderRadius: 6,
                       fontFamily: "Manrope", fontSize: 13, fontWeight: 600,
                       boxShadow: "0 6px 20px rgba(6,95,70,0.35)", zIndex: 200 }}>
          ✓ {flash}
        </div>
      )}

      {showSectionEditor && editingSection && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100,
                       display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "white", borderRadius: 10, padding: 24, width: 620, maxWidth: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontFamily: "Manrope", fontWeight: 700, fontSize: 17 }}>
                {editingSection.id ? "Редактировать раздел" : "Новый раздел"}
              </h3>
              <button onClick={() => setShowSectionEditor(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#8a8a89" }}>
                <X size={20} />
              </button>
            </div>
            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={fieldLabel}>Заголовок раздела</div>
              <input data-testid="kb-section-title"
                value={editingSection.title}
                onChange={e => setEditingSection({ ...editingSection, title: e.target.value })}
                placeholder="Например: Возврат меховых изделий" style={inputStyle} />
            </label>
            <label style={{ display: "block" }}>
              <div style={fieldLabel}>Содержимое</div>
              <textarea data-testid="kb-section-content"
                value={editingSection.content}
                onChange={e => setEditingSection({ ...editingSection, content: e.target.value })}
                rows={9}
                placeholder="Свободный текст. Можно использовать многострочный формат — ИИ прочитает всё."
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowSectionEditor(false)}
                style={{ background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
                         padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: 13 }}>
                Отмена
              </button>
              <button data-testid="kb-section-save" onClick={saveSection}
                disabled={!editingSection.title.trim() || !editingSection.content.trim()}
                style={{ ...saveBtnStyle, opacity: (!editingSection.title.trim() || !editingSection.content.trim()) ? 0.5 : 1 }}>
                <Save size={13} /> Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- */
const cardStyle = {
  background: "white", border: "1px solid #e5e7eb", borderRadius: 10,
  padding: 18,
};
const sectionHeader = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  marginBottom: 14, borderBottom: "1px solid #f5f5f4", paddingBottom: 10,
};
const saveBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
  padding: "7px 12px", borderRadius: 6, border: "none",
  fontFamily: "Manrope", fontWeight: 700, fontSize: 12, cursor: "pointer",
};
const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 6,
  fontFamily: "Manrope", fontSize: 13, outline: "none", background: "white",
  boxSizing: "border-box",
};
const fieldLabel = {
  fontFamily: "Manrope", fontSize: 10, fontWeight: 700, color: "#0A0A0A",
  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5,
};

function ContactField({ icon: Icon, label, value, onChange, testid }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
        <Icon size={12} style={{ color: "#C5A059" }} />
        <span style={fieldLabel}>{label}</span>
      </div>
      <input data-testid={testid} value={value}
        onChange={e => onChange(e.target.value)} style={inputStyle} />
    </label>
  );
}

function IconBtn({ children, disabled, ...props }) {
  return (
    <button {...props} disabled={disabled}
      style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
               padding: "6px 8px", cursor: disabled ? "not-allowed" : "pointer",
               color: disabled ? "#c4c4c4" : "#3f3f46", opacity: disabled ? 0.5 : 1,
               display: "flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </button>
  );
}
